import type { ReactNode } from 'react';
import { Box, chakra, HStack, Stack, Text } from '@chakra-ui/react';
import { IconGlyph } from '../ui/icon_glyph.tsx';

// The parts the settings are made of (docs/specs/settings.md, UI-1): groups of rows, as in the
// settings of iOS and Android. A row has an icon, a title, a line of detail, and on its right a
// chevron (it opens something), a switch or a button.

/** Rows in a rounded box, with a heading above and a note below if given. */
export function SettingsGroup({ title, note, children }: { title?: string | undefined; note?: ReactNode; children: ReactNode }) : ReactNode {
    return (
        <Stack gap="1.5">
            {title ? (
                <Text fontSize="xs" fontWeight="semibold" color="casa.groupTitle" textTransform="uppercase" letterSpacing="wider" px="3">
                    {title}
                </Text>
            ) : null}
            <Box bg="bg.panel" borderRadius="xl" borderWidth="1px" borderColor="casa.groupBorder" overflow="hidden" css={{ '& > * + *': { borderTopWidth: '1px', borderColor: 'casa.groupDivider' } }}>
                {children}
            </Box>
            {note ? (
                <Text fontSize="sm" color="fg.muted" px="3">
                    {note}
                </Text>
            ) : null}
        </Stack>
    );
}

export interface SettingsRowProps {
    /** A Font Awesome name (the dashboard's icon set). */
    icon?: string | undefined;
    title: ReactNode;
    detail?: ReactNode;
    /** Tapping the row; with a chevron unless `end` is given. */
    onClick?: (() => void) | undefined;
    /** What sits on the right: a switch, a badge, buttons. */
    end?: ReactNode;
    /** Marked as the chosen one (a tick). */
    isCurrent?: boolean;
    /** A chevron says the row opens something; not for a row that chooses or acts at once. */
    opens?: boolean;
    isSelected?: boolean;
    color?: string | undefined;
    'data-testid'?: string;
}

const RowButton = chakra('button');

export function SettingsRow({ icon, title, detail, onClick, end, isCurrent = false, opens = true, isSelected = false, color, ...rest }: SettingsRowProps) : ReactNode {
    const main = (
        <HStack gap="3" px="4" py="3" minH="14" flex="1" minW="0" textAlign="start">
            {icon ? <IconGlyph name={icon} size={22} color={color ?? '#d4d4d8'} /> : null}
            <Box flex="1" minW="0">
                <Text fontWeight="medium" color={color} truncate>
                    {title}
                </Text>
                {detail ? (
                    <Text fontSize="sm" color="fg.muted" truncate>
                        {detail}
                    </Text>
                ) : null}
            </Box>
            {isCurrent ? <IconGlyph name="check" size={18} color="#60a5fa" /> : null}
            {onClick && opens && !end ? <IconGlyph name="chevron-right" size={14} color="#71717a" /> : null}
        </HStack>
    );

    // The row's own button holds no other buttons: `end` sits beside it.
    return (
        <HStack gap="0" pe={end ? '3' : '0'} bg={isSelected ? 'bg.emphasized' : undefined} data-testid={rest['data-testid']}>
            {onClick ? (
                <RowButton display="flex" flex="1" minW="0" onClick={onClick} _hover={{ bg: isSelected ? 'bg.emphasized' : 'bg.muted' }} cursor="pointer">
                    {main}
                </RowButton>
            ) : (
                main
            )}
            {end}
        </HStack>
    );
}

/** A part's title, with a way back to the list of parts on a phone. */
export function PartHeading({ title, onBack }: { title: string; onBack?: (() => void) | undefined }) : ReactNode {
    return (
        <HStack gap="2" mb="2">
            {onBack ? (
                <RowButton onClick={onBack} aria-label="Back" display="flex" alignItems="center" gap="1" color="blue.400" px="1" py="2" cursor="pointer">
                    <Box transform="rotate(180deg)">
                        <IconGlyph name="chevron-right" size={14} color="#60a5fa" />
                    </Box>
                    <Text>Settings</Text>
                </RowButton>
            ) : null}
            <Text fontSize="2xl" fontWeight="bold" flex="1" textAlign={onBack ? 'center' : 'start'} pr={onBack ? '20' : '0'}>
                {title}
            </Text>
        </HStack>
    );
}
